import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import { resolveColor } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertCircleLine from "@store/Components/Icons/AlertCircle/AlertCircleLine";
import AlertTriangleLine from "@store/Components/Icons/AlertTriangle/AlertTriangleLine";
import CheckmarkCircle from "@store/Components/Icons/CheckmarkCircle";
import InfoLine from "@store/Components/Icons/Info/InfoLine";
import useMergeProps from "@store/Hooks/Common/useMergeProps";
import { TYPOGRAPHY } from "@store/theme/design";
import { contentSpacingStyles } from "@store/theme/mixins";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { FC } from "react";
import { css } from "styled-components";

export const enum InfosBoxType {
    Warning = "warning",
    Error = "error",
    Success = "success",
}

export const enum InfosBoxSize {
    Small = "small",
    Medium = "medium",
}

export interface InfosBoxProps {
    type?: InfosBoxType;
    size?: InfosBoxSize;
    showIcon?: boolean;
    extendedStyles?: InfosBoxStyles;
}

export interface InfosBoxStyles {
    spacing?: string;
    color?: string;
    backgroundColor?: string;
    wrapper?: InjectableCss;
    icon?: IconPresentationProps;
    content?: InjectableCss;
}

export const infosBoxStyles: InfosBoxStyles = {
    color: "info",
    backgroundColor: "info.contrast",
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            line-height: ${TYPOGRAPHY.lineHeight.small};
            ${contentSpacingStyles("1em")}

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [TYPOGRAPHY.size.smaller, TYPOGRAPHY.size.small], properties: ["font-size"] }],
                    mediaOptions.min,
                )}

            ul, ol {
                margin: 0.5em 0;
            }

            li {
                &:not(:last-child) {
                    margin-bottom: 0.25em;
                }
            }
        `,
    },
    icon: {
        src: InfoLine,
        color: "currentColor",
    },
};

export const infosBoxTypes: Partial<Record<InfosBoxType, InfosBoxStyles>> = {
    warning: {
        color: "warning",
        backgroundColor: "warning.contrast",
        icon: {
            src: AlertTriangleLine,
        },
    },
    error: {
        color: "danger",
        backgroundColor: "danger.contrast",
        icon: {
            src: AlertCircleLine,
        },
    },
    success: {
        color: "success",
        backgroundColor: "success.contrast",
        icon: {
            src: CheckmarkCircle,
        },
    },
};

export const infosBoxSizes: Partial<Record<InfosBoxSize, InfosBoxStyles>> = {
    small: {
        spacing: "0.5em",
        icon: {
            size: 24,
        },
    },
    medium: {
        spacing: "1em",
        icon: {
            size: 28,
        },
    },
};

export const InfosBox: FC<InfosBoxProps> = ({
    type,
    size = InfosBoxSize.Medium,
    showIcon = true,
    children,
    extendedStyles,
}) => {
    const sizeDefinition = size ? infosBoxSizes[size] : {};
    const typeDefinition = type ? infosBoxTypes[type] : {};
    const definitions = useMergeProps(sizeDefinition, typeDefinition);
    const stylesAndDefinitions = useMergeProps(infosBoxStyles, definitions);
    const styles = useMergeProps(stylesAndDefinitions, extendedStyles);

    if (!children) {
        return null;
    }

    const wrapperStyles = css`
        ${styles.spacing ? `padding: ${styles.spacing};` : null}
        ${props => (styles.color ? `color: ${resolveColor(styles.color, props.theme)};` : null)}
        ${props =>
            styles.backgroundColor ? `background-color: ${resolveColor(styles.backgroundColor, props.theme)};` : null}
        ${styles.wrapper?.css}
    `;

    const iconStyles = css`
        ${styles.spacing ? `margin-right: ${styles.spacing};` : null}
        ${styles.icon?.css}
    `;

    return (
        <StyledWrapper {...styles.wrapper} css={wrapperStyles}>
            {showIcon ? <Icon {...styles.icon} css={iconStyles} /> : null}
            <StyledWrapper {...styles.content}>{children}</StyledWrapper>
        </StyledWrapper>
    );
};

export default InfosBox;
